import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLead } from "@/context/LeadContext";
import { fireLead, trackEvent } from "@/lib/analytics";
import { CardByVariant } from "./Cards";

// The 60-second script — 7 acts, times cumulative from loop start.
export const SCRIPT = [
  { at: 0.0, kind: "user", text: "Take this listing live for me. Empty photos are in the folder.", act: 1 },
  { at: 1.8, kind: "typing" },
  { at: 2.4, kind: "ai", text: "On it. Staging the living room first — modern warm to match your recent work." },
  { at: 3.4, kind: "typing" },
  { at: 3.6, kind: "card", variant: "staging" },
  { at: 7.2, kind: "ai", text: "Staged. Want other vibes? Otherwise I'll keep moving." },

  { at: 9.0, kind: "user", text: "Keep going. Comps too.", act: 2 },
  { at: 10.4, kind: "ai", text: "Pulling 6 comps within a mile…" },
  { at: 11.2, kind: "typing" },
  { at: 11.4, kind: "card", variant: "comps" },
  { at: 15.0, kind: "ai", text: "You're priced dead-center. Nice." },

  { at: 17.0, kind: "user", text: "Cool. Turn the living room into a 360.", act: 3 },
  { at: 18.4, kind: "ai", text: "Grab the phone and spin slowly. I'll stitch." },
  { at: 19.2, kind: "typing" },
  { at: 19.4, kind: "card", variant: "tour" },
  { at: 23.6, kind: "ai", text: "Tour ready — I'll drop it on the listing page." },

  { at: 25.0, kind: "user", text: "Now write the launch campaign. Warm, editorial voice.", act: 4 },
  { at: 26.4, kind: "ai", text: "Warm and editorial, no salesy filler. Drafting…" },
  { at: 27.2, kind: "typing" },
  { at: 27.4, kind: "card", variant: "promo" },
  { at: 32.4, kind: "ai", text: "12 assets ready. Want me to schedule the send window?" },

  { at: 34.0, kind: "user", text: "A buyer just messaged the listing page — how hot?", act: 5 },
  { at: 35.4, kind: "ai", text: "Reading the transcript now…" },
  { at: 36.2, kind: "typing" },
  { at: 36.4, kind: "card", variant: "lead" },
  { at: 40.8, kind: "ai", text: "Call between 6-8pm. Objection they'll raise: HOA fee. Want me to prep the counter?" },

  { at: 42.0, kind: "user", text: "What is Sarah at Compass doing this week?", act: 6 },
  { at: 43.4, kind: "ai", text: "Scanning her socials + Meta ads…" },
  { at: 44.2, kind: "typing" },
  { at: 44.4, kind: "card", variant: "competitor" },
  { at: 49.0, kind: "ai", text: "Copy her rooftop-video hook but push the school-zone angle — that's your win." },

  { at: 50.0, kind: "user", text: "Nice. Text me next time she posts.", act: 7 },
  { at: 51.4, kind: "ai", text: "Set. I'll ping you within 5 minutes of any new post from her." },
  { at: 53.0, kind: "ai", text: "That's the listing done: staged, priced, 360'd, launched, scored, competitor-tracked." },
  { at: 54.4, kind: "ai", text: "You've been in one chat this whole time." },
];

export const LOOP_MS = 60000;

// Char-by-char typing hook for user messages.
const useTypedText = (text, active, cpsMs = 32) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) { setN(text.length); return; }
    setN(0);
    let i = 0;
    const t = setInterval(() => { i += 1; setN(i); if (i >= text.length) clearInterval(t); }, cpsMs);
    return () => clearInterval(t);
  }, [text, active, cpsMs]);
  return text.slice(0, n);
};

const UserMessage = ({ text, live }) => {
  const shown = useTypedText(text, live);
  return (
    <div className="ml-auto max-w-[80%] bg-coral text-bone text-[13px] leading-snug px-3 py-2 rounded-2xl rounded-br-sm">
      {shown}{live && shown.length < text.length && <span className="inline-block w-[1px] h-3 bg-bone/80 ml-0.5 animate-pulse" />}
    </div>
  );
};

const AiMessage = ({ text }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }} className="mr-auto max-w-[85%] bg-ink/5 text-ink text-[13px] leading-snug px-3 py-2 rounded-2xl rounded-bl-sm">
    {text}
  </motion.div>
);

const TypingDots = () => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mr-auto bg-ink/5 px-3 py-2.5 rounded-2xl rounded-bl-sm inline-flex gap-1">
    {[0, 1, 2].map((i) => (
      <motion.span key={i} animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.15 }} className="w-1.5 h-1.5 bg-ink/50 rounded-full" />
    ))}
  </motion.div>
);

export const ConciergeHero = ({ onOpenDemo }) => {
  const { openLead } = useLead();
  const reduced = useReducedMotion();
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const [entries, setEntries] = useState([]);
  const [activeAct, setActiveAct] = useState(1);
  const [typing, setTyping] = useState(false);
  const [key, setKey] = useState(0);
  const [visible, setVisible] = useState(true);
  const [elapsed, setElapsed] = useState(0); // seconds into current 60s loop
  const seenActs = useRef(new Set());
  const looped = useRef(false);

  // Preloaded copy of every entry with a stable id — used for reduced-motion fallback.
  const allEntries = useMemo(
    () => SCRIPT.filter((s) => s.kind !== "typing").map((s, i) => ({ ...s, id: i })),
    []
  );

  // Fire hero_view once when it first appears
  useEffect(() => {
    if (!containerRef.current) return;
    const io = new IntersectionObserver((es) => {
      const e = es[0];
      setVisible(e.isIntersecting && e.intersectionRatio >= 0.4);
      if (e.intersectionRatio >= 0.5 && !seenActs.current.has("view")) {
        seenActs.current.add("view");
        trackEvent("hero_view");
      }
    }, { threshold: [0, 0.4, 0.5] });
    io.observe(containerRef.current);
    return () => io.disconnect();
  }, []);

  // Timing loop
  useEffect(() => {
    if (reduced) return;
    if (!visible) return;
    const start = performance.now();
    const timeouts = [];
    setEntries([]);
    setTyping(false);
    setActiveAct(1);

    SCRIPT.forEach((step, idx) => {
      const t = setTimeout(() => {
        if (step.kind === "typing") {
          setTyping(true);
          const off = setTimeout(() => setTyping(false), 800);
          timeouts.push(off);
          return;
        }
        setTyping(false);
        setEntries((prev) => [...prev, { ...step, id: `${key}-${idx}` }]);
        if (step.act) {
          setActiveAct(step.act);
          if (!seenActs.current.has(step.act)) {
            seenActs.current.add(step.act);
            trackEvent("hero_act_reach", { act: step.act });
          }
        }
      }, step.at * 1000);
      timeouts.push(t);
    });
    // full loop event + restart
    const loopEnd = setTimeout(() => {
      if (!looped.current) { looped.current = true; trackEvent("hero_full_loop"); }
      setKey((k) => k + 1);
    }, LOOP_MS);
    timeouts.push(loopEnd);
    return () => timeouts.forEach(clearTimeout);
  }, [key, visible, reduced]);

  // Auto-scroll on new entry / typing
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [entries, typing]);

  // Live 0:00 → 1:00 playback timer, synced to loop start (rAF for smoothness)
  useEffect(() => {
    if (reduced || !visible) { setElapsed(0); return; }
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const s = Math.min(60, (t - start) / 1000);
      setElapsed(s);
      if (s < 60) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [key, visible, reduced]);

  // Reduced-motion fallback: fire once and render static full transcript
  useEffect(() => { if (reduced) trackEvent("hero_reduced_motion"); }, [reduced]);

  const onPrimary = () => {
    trackEvent("hero_cta_primary");
    fireLead({ source: "concierge-hero" });
    openLead({ source: "concierge-hero" });
  };
  const onSecondary = () => {
    trackEvent("hero_cta_secondary");
    if (onOpenDemo) return onOpenDemo();
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderList = reduced ? allEntries : entries;
  const mm = Math.floor(elapsed / 60);
  const ss = Math.floor(elapsed % 60);
  const clock = `${mm}:${String(ss).padStart(2, "0")}`;
  const R = 7, CIRC = 2 * Math.PI * R;
  const ringOffset = CIRC - (Math.min(elapsed, 60) / 60) * CIRC;

  return (
    <section ref={containerRef} className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden" data-testid="concierge-hero">
      <a href="#post-hero" className="sr-only focus:not-sr-only fixed top-2 left-2 z-50 bg-ink text-bone px-3 py-2 text-xs">Skip animated demo</a>
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <p className="text-xs uppercase tracking-[0.2em] text-ink/50 mb-6">Meet the concierge</p>
          <h1 className="font-serif text-[13vw] sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink">
            One conversation.<br />
            <span className="italic text-coral">A whole listing done.</span>
          </h1>
          <p className="mt-7 max-w-xl text-base md:text-lg text-ink/70 leading-relaxed">
            You talk. Chateaux stages the room, pulls the comps, drafts the campaign, scores the lead. You never leave the chat.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button onClick={onPrimary} data-testid="concierge-hero-cta-primary" className="group inline-flex items-center justify-center gap-2 bg-coral text-bone px-7 py-4 text-sm font-medium hover:bg-ink transition-colors duration-300">
              Start free <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={onSecondary} data-testid="concierge-hero-cta-secondary" className="group inline-flex items-center justify-center gap-2.5 border border-ink/25 text-ink px-7 py-4 text-sm font-medium hover:bg-ink hover:text-bone transition-colors duration-300">
              <span className="relative w-4 h-4 shrink-0" data-testid="concierge-timer-ring" aria-hidden="true">
                <svg viewBox="0 0 16 16" className="w-full h-full -rotate-90">
                  <circle cx="8" cy="8" r={R} fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1.5" />
                  <circle cx="8" cy="8" r={R} fill="none" stroke="#FF5A36" strokeWidth="1.5" strokeLinecap="round"
                          strokeDasharray={CIRC} strokeDashoffset={ringOffset}
                          style={{ transition: reduced ? "none" : "stroke-dashoffset 0.1s linear" }} />
                </svg>
              </span>
              Watch the 60-sec conversation
              <span data-testid="concierge-timer-clock" className="font-mono text-[11px] tabular-nums text-ink/60 group-hover:text-bone/70 transition-colors">
                {clock}<span className="opacity-40"> / 1:00</span>
              </span>
            </button>
          </div>
        </div>

        {/* Phone mock */}
        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <div className="relative w-[290px] sm:w-[340px] h-[600px] sm:h-[720px] rounded-[44px] bg-ink p-3 shadow-2xl">
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-ink rounded-b-2xl z-20" />
            <div className="w-full h-full rounded-[34px] overflow-hidden bg-bone flex flex-col">
              {/* Chat header */}
              <div className="flex items-center gap-2 px-3 py-3 border-b border-ink/10 shrink-0">
                <div className="w-7 h-7 rounded-full bg-coral flex items-center justify-center font-serif text-bone text-sm">C</div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-ink font-medium leading-none">Concierge</p>
                  <p className="text-[9px] text-ink/50 mt-0.5 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> online</p>
                </div>
              </div>
              {/* Transcript */}
              <div ref={scrollRef} data-testid="concierge-transcript" className="flex-1 overflow-y-auto px-3 py-3 space-y-2 scroll-smooth">
                {renderList.map((e) => (
                  <div key={e.id} className="flex flex-col">
                    {e.kind === "user" && <UserMessage text={e.text} live={!reduced && e === entries[entries.length - 1]} />}
                    {e.kind === "ai" && <AiMessage text={e.text} />}
                    {e.kind === "card" && <div className="my-1"><CardByVariant variant={e.variant} /></div>}
                  </div>
                ))}
                {typing && <TypingDots />}
              </div>
              {/* Input strip (ghost) */}
              <div className="border-t border-ink/10 px-3 py-2.5 flex items-center gap-2 shrink-0">
                <span className="flex-1 text-[11px] text-ink/40">ask anything…</span>
                <div className="w-6 h-6 rounded-full bg-ink flex items-center justify-center text-bone text-[10px]">▲</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress dots (§1) */}
      <div className="mt-8 flex justify-center gap-2" data-testid="concierge-dots">
        {[1, 2, 3, 4, 5, 6, 7].map((a) => (
          <button
            key={a}
            onClick={() => { trackEvent("hero_dot_click", { act: a }); setKey((k) => k + 1); }}
            aria-label={`Jump to act ${a}`}
            aria-current={a === activeAct ? "step" : undefined}
            className={`w-2 h-2 rounded-full transition-all ${a === activeAct ? "bg-coral ring-2 ring-coral/30 ring-offset-1 ring-offset-bone scale-125" : a < activeAct ? "bg-coral/70" : "bg-ink/20 hover:bg-ink/40"}`}
          />
        ))}
      </div>

      <p className="mt-8 text-center text-xs text-ink/45 max-w-md mx-auto">
        Every card above is a real app screen. Every line is a real Claude response.
      </p>
    </section>
  );
};
